import type { ChannelHealth, ChannelSummary } from "../../../api/types";

/** FB2-51: minutes without any inbound webhook before an "active" Cloud channel is flagged. */
export const WEBHOOK_SILENT_MINUTES = 30;

export type ChannelProblemReason =
  | "disconnected"
  | "needsReauth"
  | "tokenExpired"
  | "webhookSilent"
  | "qrFailed"
  | "qrStopped"
  | "threadOwnerConflict";

export interface ChannelProblem {
  readonly channelId: string;
  readonly reason: ChannelProblemReason;
}

/** Who may fix a channel: owners/managers or anyone granted a channel-write permission. */
export function canFixChannels(permissions: {
  readonly manage: boolean;
  readonly granted?: readonly string[];
}): boolean {
  const granted = permissions.granted ?? [];
  return (
    permissions.manage || granted.includes("channels.create") || granted.includes("channels.edit")
  );
}

/**
 * Pure classification of one channel. `health` is only needed (and only fetched) for a Cloud
 * channel that is marked active; QR channels are judged by their status alone.
 */
export function classifyChannel(
  channel: Pick<
    ChannelSummary,
    "id" | "provider" | "status" | "needsReauth" | "threadOwnerConflict"
  >,
  health: Pick<ChannelHealth, "lastWebhookAt" | "webhookSilent" | "phoneStatus"> | null,
  now: number = Date.now(),
): ChannelProblem | null {
  const status = channel.status.toLowerCase();
  const problem = (reason: ChannelProblemReason): ChannelProblem => ({
    channelId: channel.id,
    reason,
  });
  if (channel.provider === "whatsapp_waha") {
    if (status === "failed") return problem("qrFailed");
    if (status === "stopped") return problem("qrStopped");
    return null;
  }
  if (channel.provider === "instagram") {
    if (channel.needsReauth === true) return problem("needsReauth");
    return channel.threadOwnerConflict === true ? problem("threadOwnerConflict") : null;
  }
  if (channel.provider !== "whatsapp_cloud") return null;
  // OWN-153: auth errors tripped the shared ChannelAuthHealth flag; the owner must reconnect.
  if (channel.needsReauth === true) return problem("needsReauth");
  if (status.includes("token") || status.includes("expired")) return problem("tokenExpired");
  if (status === "disconnected" || health?.phoneStatus?.toUpperCase() === "DISCONNECTED") {
    return problem("disconnected");
  }
  if (status !== "connected" || !health) return null;
  const last = health.lastWebhookAt ? Date.parse(health.lastWebhookAt) : null;
  const stale = last !== null && now - last > WEBHOOK_SILENT_MINUTES * 60_000;
  return health.webhookSilent || stale ? problem("webhookSilent") : null;
}
